import { BroadbandLabelModel, CardModel } from "atoms/models";
import { Accordion, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useRecoilState } from "recoil";
import { broadbandLabelAccordionOpenState } from "atoms";

interface CardBroadbandLabelProps {
  broadbandLabel: BroadbandLabelModel;
  card?: CardModel;
  bannerOffer?: Boolean;
}

const CardBroadbandLabel: React.FC<CardBroadbandLabelProps> = ({ broadbandLabel, card, bannerOffer }) => {
  const [broadbandLabelAccordionOpen, setBroadbandLabelAccordionOpen] = useRecoilState(broadbandLabelAccordionOpenState(card?.id));
  const scrollToTopOfCard = () => {
    const targetDivForNearestElement = document.getElementById(`broadbandLabel-${broadbandLabel.id}`);

    // Check if the element exists
    if (targetDivForNearestElement) {
      // Get the current scroll position of the window
      const scrollPosition = window.scrollY || window.pageYOffset;

      // Calculate the target position of the nearest card relative to the button
      // Adjusted by -270 pixels to reach the top of the card
      const targetPosition = targetDivForNearestElement.getBoundingClientRect().top + scrollPosition - 390;

      // Scroll to the calculated target position smoothly
      window.scrollTo({ top: targetPosition, behavior: "smooth" });
    }
  };

  const installationPriceTooltip = (
    <Tooltip id={`tooltip-${broadbandLabel.id}`}>
      This fee may be waived in various cases such as with promotional offerings.{" "}
      {broadbandLabel?.installationPrice
        ? broadbandLabel.installationPrice.toLocaleString("en-US", { style: "currency", currency: "USD" })
        : null}{" "}
      includes one connection point. Wall-fishing may incur additional charges.
    </Tooltip>
  );
  const securityDepositTooltip = (
    <Tooltip id={`tooltip-${broadbandLabel.id}`}>
      This amount is determined based on the results of a credit check and may not apply in various cases (promos, good credit check, ACP,
      etc).
    </Tooltip>
  );

  return (
    <Accordion defaultActiveKey={bannerOffer ? "0" : "1"} flush className='broadband-accordion' id={`broadbandLabel-${broadbandLabel.id}`}>
      <Accordion.Item eventKey='0'>
        <div className='d-flex justify-content-center'>
          <Accordion.Header
            onClick={() => {
              setBroadbandLabelAccordionOpen(!broadbandLabelAccordionOpen);
              scrollToTopOfCard();
            }}
            className='mb-2'>
            {broadbandLabelAccordionOpen ? "Hide Broadband Facts" : "View Broadband Facts"}
          </Accordion.Header>
        </div>
        <Accordion.Body>
          <div className='broadband-label-container'>
            <div className='card-broadband-banner p-3'>
              <table className='table table-borderless'>
                <tbody>
                  <tr>
                    <td colSpan={2} className='border-top-0 text-center'>
                      <h1 className='broadband-facts-header'>Broadband Facts</h1>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className='text-align-left padding-left border-top-0 broadband-company-details'>
                      {broadbandLabel.providerName}
                      <br />
                      {broadbandLabel.servicePlanName}
                      <br />
                      Fixed Broadband Consumer Disclosure
                    </td>
                  </tr>
                  <tr className='monthly-price-row broadband-monthly-price'>
                    <th scope='row' className='thick-border'>
                      Monthly Price
                    </th>
                    <th scope='row' className='text-align-right thick-border'>
                      {broadbandLabel?.monthlyPrice
                        ? broadbandLabel.monthlyPrice.toLocaleString("en-US", { style: "currency", currency: "USD" })
                        : null}
                    </th>
                  </tr>
                  <tr>
                    <td colSpan={2} className='text-align-left padding-left less-thick-border'>
                      {broadbandLabel.monthlyPriceText}
                    </td>
                  </tr>
                  <tr>
                    <th scope='row' colSpan={2}>
                      Additional Charges and Terms
                    </th>
                  </tr>
                  <tr>
                    <th scope='row' className='border-top-0 padding-left font-weight-normal' colSpan={2}>
                      Provider Monthly Fees
                    </th>
                  </tr>
                  <tr>
                    <th scope='row' className='border-top-0 padding-left-sub-item font-weight-normal'>
                      Late Fees
                    </th>
                    <td className='border-top-0 text-align-right padding-bottom-0 border-top-0' style={{ width: "200px" }}>
                      {broadbandLabel.latePaymentPercent ? <span>{broadbandLabel.latePaymentPercent}% of past due balance</span> : null}
                    </td>
                  </tr>
                  <tr>
                    <th scope='row' className='border-top-0 padding-left font-weight-normal' colSpan={2}>
                      One-Time Fees at the Time of Purchase
                    </th>
                  </tr>
                  <tr>
                    <th scope='row' className='border-top-0 padding-left-sub-item font-weight-normal padding-bottom-0'>
                      Installation
                    </th>
                    <td className='border-top-0 text-align-right padding-bottom-0 border-top-0'>
                      {broadbandLabel.installationPrice
                        ? broadbandLabel.installationPrice.toLocaleString("en-US", { style: "currency", currency: "USD" })
                        : null}
                      <OverlayTrigger placement='top' overlay={installationPriceTooltip}>
                        <i style={{ fontSize: "10px", marginLeft: "5px" }} className='fa-solid fa-asterisk'></i>
                      </OverlayTrigger>
                    </td>
                  </tr>
                  <tr>
                    <th scope='row' className='border-top-0 padding-left-sub-item font-weight-normal padding-top-0 border-top-0'>
                      <div style={{ width: "110px" }}>Security Deposit</div>
                    </th>
                    <td className='border-top-0 text-align-right padding-top-0 border-top-0'>
                      {broadbandLabel.securityDepositPrice
                        ? broadbandLabel.securityDepositPrice.toLocaleString("en-US", { style: "currency", currency: "USD" })
                        : null}
                      <OverlayTrigger placement='top' overlay={securityDepositTooltip}>
                        <i style={{ fontSize: "10px", marginLeft: "5px" }} className='fa-solid fa-asterisk'></i>
                      </OverlayTrigger>
                    </td>
                  </tr>
                  <tr>
                    <th scope='row' className='border-top-0 padding-left font-weight-normal'>
                      <div style={{ width: "200px" }}>Early Termination Fee</div>
                    </th>
                    <td className='border-top-0 text-align-right'>
                      {broadbandLabel?.earlyTerminationPrice
                        ? broadbandLabel.earlyTerminationPrice.toLocaleString("en-US", { style: "currency", currency: "USD" })
                        : "$0.00"}
                    </td>
                  </tr>
                  <tr>
                    <th scope='row' className='border-top-0 padding-left font-weight-normal'>
                      Government Taxes
                    </th>
                    <td className='border-top-0 text-align-right'>Varies by Location</td>
                  </tr>
                  <tr>
                    <th scope='row' colSpan={2} className='thin-border'>
                      Speeds Provided With Plan
                    </th>
                  </tr>
                  <tr>
                    <th scope='row' className='border-top-0 padding-left font-weight-normal'>
                      Typical Download Speed
                    </th>
                    <td className='border-top-0 text-align-right'>{broadbandLabel.typicalDownloadSpeed}</td>
                  </tr>
                  <tr>
                    <th scope='row' className='border-top-0 padding-left font-weight-normal'>
                      Typical Upload Speed
                    </th>
                    <td className='border-top-0 text-align-right'>{broadbandLabel.typicalUploadSpeed}</td>
                  </tr>
                  <tr>
                    <th scope='row' className='border-top-0 padding-left font-weight-normal'>
                      Typical Latency
                    </th>
                    <td className='border-top-0 text-align-right'>{broadbandLabel.typicalLatency}</td>
                  </tr>
                  <tr>
                    <th scope='row'>Data Included With Monthly Price</th>
                    <td className='text-align-right'>{broadbandLabel.dataIncluded}</td>
                  </tr>
                  <tr>
                    <th colSpan={2} scope='row' className='thin-border'>
                      <div className='network-link-container'>
                        <div className='network-link-title'>Network Management</div>
                        <div className='network-link'>
                          <a href='https://comporium.link/nmd' target='_blank' rel='noreferrer'>
                            Review Our Policy
                          </a>
                        </div>
                      </div>
                    </th>
                  </tr>
                  <tr>
                    <th colSpan={2} scope='row' className='thin-border'>
                      <div className='network-link-container'>
                        <div className='network-link-title'>Privacy</div>
                        <div className='network-link'>
                          <a href={broadbandLabel.privacyPolicyUrl} target='_blank' rel='noreferrer'>
                            Review Our Policy
                          </a>
                        </div>
                      </div>
                    </th>
                  </tr>
                  <tr>
                    <th scope='row' className='thick-border'>
                      Customer Support
                    </th>
                    <th scope='row' className='thick-border text-end'>
                      <a href={broadbandLabel.customerSupportUrl} target='_blank' rel='noreferrer'>
                        Contact Us
                      </a>
                    </th>
                  </tr>
                  <tr>
                    <td colSpan={2} className='text-align-left less-thick-border'>
                      Learn more about the terms used on this label by visiting the Federal Communications Commission’s Consumer Resource
                      Center.
                      <br />
                      <div className='fcc-link'>
                        <a href='https://www.fcc.gov/consumers' target='_blank' rel='noreferrer'>
                          fcc.gov/consumers
                        </a>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className='text-center'>
                      <div className='plan-id'>{broadbandLabel.uniquePlanID}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default CardBroadbandLabel;
